/* Store page behaviour: scene crossfade, horizontal strips, expandable interest
 * cards, and hover-to-play video.
 *
 * Same lazy-registry approach as homa-home.js — nothing initialises until the
 * section is near the viewport.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const q = (sel, root = document) => root.querySelector(sel);
const qa = (sel, root = document) => [...root.querySelectorAll(sel)];

const registry = [];
const register = (selector, init) => registry.push({ selector, init });

// Sections that stack images at opacity 0 ship all but the visible one inside
// an inert <template>, because a transparent element is still in the viewport
// and would defeat loading="lazy". Swap the real markup in on demand.
function hydrate(scope) {
  if (!scope) return;
  for (const tpl of qa('[data-hf-deferred-media]', scope)) {
    tpl.replaceWith(tpl.content.cloneNode(true));
  }
}

function observeOnce(el, fn) {
  if (!('IntersectionObserver' in window)) return fn();
  const io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      fn();
    },
    { rootMargin: '200px' }
  );
  io.observe(el);
}

/* ── Hero: scene tabs + day/dusk mode overlays ──────────────── */

register('[data-hf-store-hero]', (hero) => {
  const layers = qa('[data-hf-store-scene]', hero);
  const tabs = qa('[data-hf-store-tab]', hero);
  const modeLayers = qa('[data-hf-store-mode]', hero);
  const pills = qa('[data-hf-store-pill]', hero);
  const timeBtns = qa('[data-hf-store-time-btn]', hero);
  let activeScene = 0;
  let activeMode = 0;
  let timeMode = hero.dataset.hfStoreTime || 'day';

  // Keep every layer in the layout (opacity only) so browsers download them
  // and crossfades never drop to the black hero background mid-switch.
  function preloadHeroImages() {
    const imgs = qa('img', hero.querySelector('[data-hf-store-zoom]') || hero);
    imgs.forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
      if (img.decode) img.decode().catch(() => {});
      else if (!img.complete && img.src) {
        const warm = new Image();
        warm.src = img.currentSrc || img.src;
      }
    });
  }

  function showMode(modeIndex, time) {
    modeLayers.forEach((layer) => {
      const mi = Number(layer.dataset.hfStoreMode);
      const t = layer.dataset.hfStoreTimeLayer;
      const on = activeScene === 0 && mi === modeIndex && t === time;
      layer.classList.toggle('is-active', on);
      layer.toggleAttribute('aria-hidden', !on);
    });
  }

  const pinsLayer = q('[data-hf-store-pins]', hero);
  const pins = qa('[data-hf-store-pin]', hero);

  function syncPins() {
    if (!pinsLayer) return;
    const show = activeScene === 0 && !hero.classList.contains('is-zoomed');
    pinsLayer.hidden = !show;
    if (!show) pins.forEach((p) => p.classList.remove('is-open'));
  }

  function setScene(i) {
    activeScene = i;
    hero.classList.toggle('is-alt-scene', i !== 0);

    tabs.forEach((t, x) => {
      const on = x === i;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    });

    layers.forEach((l) => {
      const idx = Number(l.dataset.hfStoreScene);
      const on = i !== 0 && idx === i;
      l.classList.toggle('is-active', on);
      l.toggleAttribute('aria-hidden', !on);
    });

    if (i === 0) {
      showMode(activeMode, timeMode);
      pills.forEach((p, x) => p.setAttribute('aria-pressed', String(x === activeMode)));
    } else {
      modeLayers.forEach((m) => {
        m.classList.remove('is-active');
        m.setAttribute('aria-hidden', 'true');
      });
      pills.forEach((p) => p.setAttribute('aria-pressed', 'false'));
    }
    syncPins();
  }

  // Touch / keyboard open for pin hover cards; stopPropagation so room zoom
  // does not fire when interacting with a pin. Match Store.jsx: while a pin is
  // open, room hotspots stop receiving pointer events.
  const roomsLayer = q('[data-hf-store-rooms]', hero);

  function setActivePin(pin) {
    pins.forEach((p) => p.classList.toggle('is-open', p === pin));
    if (roomsLayer) roomsLayer.style.pointerEvents = pin ? 'none' : '';
  }

  pins.forEach((pin) => {
    pin.addEventListener('pointerenter', () => setActivePin(pin));
    pin.addEventListener('pointerleave', () => {
      if (pin.classList.contains('is-open')) setActivePin(null);
    });
    pin.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = pin.classList.contains('is-open');
      setActivePin(open ? null : pin);
    });
    const label = q('.hf-store-pin__label', pin);
    label?.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      setActivePin(pin.classList.contains('is-open') ? null : pin);
    });
  });

  function setTime(next) {
    timeMode = next;
    hero.dataset.hfStoreTime = next;
    timeBtns.forEach((btn) => {
      const on = btn.dataset.hfStoreTimeBtn === next;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    });
    if (activeScene === 0) showMode(activeMode, timeMode);
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => setScene(i));
  });

  timeBtns.forEach((btn) => {
    btn.addEventListener('click', () => setTime(btn.dataset.hfStoreTimeBtn));
  });

  function setMode(i) {
    const total = pills.length;
    if (!total) return;
    activeMode = ((i % total) + total) % total;
    if (activeScene !== 0) setScene(0);
    else {
      showMode(activeMode, timeMode);
      pills.forEach((p, x) => p.setAttribute('aria-pressed', String(x === activeMode)));
    }
    pills[activeMode]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  pills.forEach((pill, i) => {
    pill.addEventListener('click', () => setMode(i));
  });

  // Step active mode (and keep the pressed pill in view). Scroll alone is a
  // no-op when the strip fits the viewport — the previous rail target also
  // couldn't scroll because overflow lived on the inner list.
  q('[data-hf-store-prev]', hero)?.addEventListener('click', () => setMode(activeMode - 1));
  q('[data-hf-store-next]', hero)?.addEventListener('click', () => setMode(activeMode + 1));

  /* Room zoom — ported from Store.jsx (a6d7054). */
  const zoomWrap = q('[data-hf-store-zoom]', hero);
  const rooms = qa('[data-hf-store-room]', hero);
  const roomScenes = qa('[data-hf-store-room-scene]', hero);
  const backBtn = q('[data-hf-store-room-back]', hero);
  let zoomTimer = 0;
  let zoomed = false;

  function warmRoom(i) {
    const scene = roomScenes[i];
    if (!scene) return;
    qa('img', scene).forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
      if (img.decode) img.decode().catch(() => {});
    });
  }

  function showRoomScene(i) {
    roomScenes.forEach((el, x) => {
      const on = x === i;
      el.classList.toggle('is-active', on);
      el.hidden = !on;
    });
  }

  function clearZoomTimer() {
    if (zoomTimer) {
      window.clearTimeout(zoomTimer);
      zoomTimer = 0;
    }
  }

  function zoomTo(room) {
    if (!zoomWrap || zoomed) return;
    zoomed = true;
    const i = Number(room.dataset.hfStoreRoomI);
    const scale = room.dataset.hfStoreZoomScale || '2.8';
    const x = room.dataset.hfStoreZoomX || '0';
    const y = room.dataset.hfStoreZoomY || '0';
    warmRoom(i);
    showRoomScene(i);
    hero.classList.add('is-zoomed');
    syncPins();
    zoomWrap.style.transform = `scale(${scale}) translate(${x}%, ${y}%)`;
    const delay = REDUCED.matches ? 0 : 900;
    clearZoomTimer();
    zoomTimer = window.setTimeout(() => {
      hero.classList.add('is-showing-room');
      if (backBtn) backBtn.hidden = false;
    }, delay);
  }

  function zoomBack() {
    if (!zoomed) return;
    hero.classList.remove('is-showing-room');
    if (backBtn) backBtn.hidden = true;
    const delay = REDUCED.matches ? 0 : 500;
    clearZoomTimer();
    zoomTimer = window.setTimeout(() => {
      hero.classList.remove('is-zoomed');
      if (zoomWrap) zoomWrap.style.transform = '';
      roomScenes.forEach((el) => {
        el.classList.remove('is-active');
        el.hidden = true;
      });
      zoomed = false;
      syncPins();
    }, delay);
  }

  rooms.forEach((room) => {
    room.addEventListener('pointerenter', () => warmRoom(Number(room.dataset.hfStoreRoomI)), {
      once: true,
    });
    room.addEventListener('click', () => zoomTo(room));
  });
  backBtn?.addEventListener('click', zoomBack);
  roomScenes.forEach((scene) => scene.addEventListener('click', zoomBack));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && zoomed) zoomBack();
  });

  const applyScene = setScene;
  setScene = (i) => {
    if (zoomed) zoomBack();
    applyScene(i);
  };

  // Match Store.jsx defaults: Smart Home + Coming Home + day.
  applyScene(0);
  setTime(timeMode);
  preloadHeroImages();
});

/* ── Horizontal strips ──────────────────────────────────────── */

register('[data-hf-strip]', (section) => {
  // A section can hold several independent rows, each with its own arrows.
  const tracks = qa('[data-hf-strip-track]', section);

  tracks.forEach((track) => {
    const viewport = track.closest('.hf-strip__viewport') || section;
    const prev = q('[data-hf-strip-prev]', viewport);
    const next = q('[data-hf-strip-next]', viewport);

    const step = () => {
      const card = track.firstElementChild;
      if (!card) return track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth - 1;
      prev?.toggleAttribute('disabled', track.scrollLeft <= 0);
      next?.toggleAttribute('disabled', track.scrollLeft >= max);
    };

    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    sync();
  });
});

/* ── Expandable interest cards ──────────────────────────────── */

register('[data-hf-interest]', (card) => {
  const panel = q('[data-hf-interest-panel]', card);
  if (!panel) return;

  const toggles = qa('[data-hf-interest-toggle]', card);
  const primary = toggles[0];

  const setOpen = (open) => {
    if (open) hydrate(card);
    card.classList.toggle('is-open', open);
    panel.hidden = !open;
    primary?.setAttribute('aria-expanded', String(open));
  };

  // Fetch the expanded image on hover so the swap is not visibly empty.
  primary?.addEventListener('pointerenter', () => hydrate(card), { once: true });

  toggles.forEach((t) =>
    t.addEventListener('click', () => setOpen(panel.hidden))
  );

  card.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !panel.hidden) {
      setOpen(false);
      primary?.focus();
    }
  });
});

/* ── Hover-to-play video cards ──────────────────────────────── */

register('[data-hf-latest-card]', (card) => {
  const video = q('[data-hf-latest-video]', card);
  if (!video || REDUCED.matches) return;

  let loaded = false;

  const start = () => {
    if (!loaded) {
      video.src = video.dataset.src;
      loaded = true;
    }
    card.classList.add('is-playing');
    video.play().catch(() => card.classList.remove('is-playing'));
  };

  const stop = () => {
    card.classList.remove('is-playing');
    video.pause();
    video.currentTime = 0;
  };

  card.addEventListener('mouseenter', start);
  card.addEventListener('mouseleave', stop);
  card.addEventListener('focusin', start);
  card.addEventListener('focusout', stop);
});

/* ── Boot ───────────────────────────────────────────────────── */

function init() {
  for (const { selector, init: fn } of registry) {
    for (const el of qa(selector)) {
      if (el.dataset.hfReady === '1') continue;
      el.dataset.hfReady = '1';
      observeOnce(el, () => fn(el));
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
